// Файл toast_provider.js

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    get_interface_customization_settings,
} from '../../shared_code/interface_customization';
import {
    dispatch_toast_event,
    normalize_toast_message,
} from './toast_message';

import './toast.css';



const ToastContext = createContext(null);

const error_markers = [
    'ошиб',
    'не удалось',
    'невозможно',
    'отклон',
    'нет доступа',
    'не найден',
    'не установ',
    'недоступ',
    'запрещ',
    'отсутств',
    'error',
    'failed',
    'failure',
    'exception',
    'http 4',
    'http 5',
];
const warning_markers = [
    'внимание',
    'предупреж',
    'проверьте',
    'выберите',
    'укажите',
];
const toast_duration_ms_setting = 'toast_duration_ms';
const toast_copy_aria_label = (
    'Копировать текст ' + 'уведомления'
);
const toast_copied_aria_label = (
    'Текст уведомления ' + 'скопирован'
);

const legacy_copy_to_clipboard = (text) => {
    const buffer = document.createElement('textarea');
    buffer.value = text;
    buffer.className = 'toast__copy-buffer';
    buffer.setAttribute('readonly', '');
    document.body.appendChild(buffer);
    buffer.select();

    try {
        return document.execCommand('copy');

    } catch {
        return false;

    } finally {
        buffer.remove();
    }
};

const copy_to_clipboard = async (message) => {
    const text = String(message || '');

    if (!text) {
        return false;
    }

    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);

            return true;

        } catch {
            return legacy_copy_to_clipboard(text);
        }
    }

    return legacy_copy_to_clipboard(text);
};

const normalize_toast_type = (message, type) => {
    const explicit_type = String(type || '').trim().toLowerCase();

    if (['error', 'danger', 'failure', 'failed'].includes(explicit_type)) {
        return 'error';
    }

    if (['warning', 'warn'].includes(explicit_type)) {
        return 'warning';
    }

    const message_text = String(message || '').toLowerCase();
    const message_is_error = error_markers.some(
        (marker) => message_text.includes(marker)
    );

    if (message_is_error) {
        return 'error';
    }

    if (['success', 'info'].includes(explicit_type)) {
        return explicit_type;
    }

    if (warning_markers.some((marker) => message_text.includes(marker))) {
        return 'warning';
    }

    return 'success';
};

const ToastProvider = ({
    children,
    show_close_button = true,
    close_button_position = 'right',
    show_copy_button = true,
}) => {
    const [toasts, setToasts] = useState([]);
    const idRef = useRef(1);

    const remove = useCallback(
        (id) => {
            setToasts(
                (prev) =>
                    prev.filter(
                        (t) => t.id !== id
                    )
            );
        }, []
    );

    const mark_copied = useCallback((id) => {
        setToasts((prev) => prev.map((toast) => (
            toast.id === id
                ? { ...toast, copied: true }
                : toast
        )));
    }, []);

    const showToast = useCallback(
        (message, type, options = {}) => {
            if (dispatch_toast_event(message)) {
                return () => {};
            }

            const id = idRef.current++;
            const duration = Number(options.duration)
                || get_interface_customization_settings()[
                    toast_duration_ms_setting
                ];
            const normalized_message = normalize_toast_message(message);
            const normalized_type = normalize_toast_type(
                normalized_message,
                type,
            );
            setToasts((prev) =>
                [...prev, {
                    id,
                    message: normalized_message,
                    type: normalized_type,
                    leaving: false,
                    copied: false,
                }]);

            const t1 = setTimeout(() => {
                setToasts((prev) =>
                    prev.map((t) =>
                        (t.id === id ? { ...t, leaving: true } : t)));
            }, duration);

            const t2 = setTimeout(
                () => remove(id), duration + 220);

            return () => {
                clearTimeout(t1);
                clearTimeout(t2);
                remove(id);
            };
        }, [remove]
    );

    const value = useMemo(() =>
        ({ showToast }), [showToast]);
    const close_button_is_left = String(close_button_position)
        .trim()
        .toLowerCase() === 'left';

    return (
        <ToastContext.Provider value={value}>
            {children}
            <div className="toast-stack flex-column">
                {toasts.map((t) => {
                    const copy_title = t.copied
                        ? 'Скопировано'
                        : 'Копировать';
                    const copy_label = t.copied
                        ? toast_copied_aria_label
                        : toast_copy_aria_label;
                    const copy_button = show_copy_button ? (
                        <button
                            type="button"
                            className="toast__copy"
                            aria-label={copy_label}
                            title={copy_title}
                            onClick={async () => {
                                const copied = await copy_to_clipboard(
                                    t.message,
                                );

                                if (copied) {
                                    mark_copied(t.id);
                                }
                            }}
                        >
                            {t.copied ? '✓' : '⧉'}
                        </button>
                    ) : null;
                    const close_button = show_close_button ? (
                        <button
                            type="button"
                            className="toast__close"
                            aria-label="Закрыть уведомление"
                            title="Закрыть"
                            onClick={() => remove(t.id)}
                        >
                            ×
                        </button>
                    ) : null;
                    const action_buttons = (
                        copy_button || close_button
                    ) ? (
                        <span className="toast__actions">
                            {copy_button}
                            {close_button}
                        </span>
                    ) : null;

                    return (
                        <div
                            key={t.id}
                            className={[
                                'toast',
                                `toast--${t.type}`,
                                t.leaving
                                    ? 'toast--leave'
                                    : 'toast--enter',
                            ].join(' ')}
                        >
                            {close_button_is_left && action_buttons}
                            <span className="toast__message">
                                {t.message}
                            </span>
                            {!close_button_is_left && action_buttons}
                        </div>
                    );
                })}
            </div>
        </ToastContext.Provider>
    );
};


const useToast = () => {
    const ctx = useContext(ToastContext);

    if (!ctx)
        throw new Error('useToast must be used inside ');

    return ctx;
};


export {
    ToastProvider,
    toast_duration_ms_setting,
    useToast,
};
